import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { PostComment } from "@domain/post/comment/postComment";
import { Person } from "@domain/user/user";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { CommentEditor } from "./commentEditor";
import { CommentView } from "./commentView";
import { RepliesView } from "./repliesView";

interface ParentCommentViewProps extends React.HTMLAttributes<HTMLDivElement> {
	postComment: PostComment;
	postId: string;
}
export const ParentCommentView: React.FC<ParentCommentViewProps> = ({ postComment, postId, ...htmlProps }) => {
	const { children } = postComment;
	const [editMode, setEditMode] = useState(false);
	const [replyTo, setReplyTo] = useState<Person | undefined>();
	const [repliesVisible, setRepliesVisible] = useState(false);

	const blockGuest = useAuthModalBlocker();

	if (editMode) {
		return (
			<StyledCommentEditor
				postId={postId}
				onCommentSubmit={() => setEditMode(false)}
				onCancel={() => setEditMode(false)}
				initialComment={postComment}
			/>
		);
	}

	return (
		<>
			<CommentView
				postComment={postComment}
				postId={postId}
				replyTo={replyTo}
				onReply={setReplyTo}
				onEdit={() => setEditMode(true)}
				{...htmlProps}
			/>
			<RepliesContainer>
				{children.last && !repliesVisible && (
					<TeasingSubCommentView
						postComment={children.last}
						postId={postId}
						replyTo={replyTo}
						onReply={() => setReplyTo(children.last?.author)}
					/>
				)}
				{repliesVisible && (
					<RepliesView
						postId={postId}
						parentId={postComment.id}
						replyTo={replyTo}
						onReply={setReplyTo}
						lastReply={children.last}
					/>
				)}
				{children.total > 1 && !repliesVisible && (
					<ShowReplies onClick={() => blockGuest(() => setRepliesVisible(true))}>
						{children.total === 1
							? I18n.t("post.comment.reply.show_one")
							: I18n.t("post.comment.reply.show", { count: children.total })}
					</ShowReplies>
				)}
				{(repliesVisible || children.last) && <Wire extended={!!replyTo} />}
			</RepliesContainer>
			{replyTo ? (
				<StyledCommentEditor
					postId={postId}
					onCommentSubmit={() => {
						setReplyTo(undefined);
						setRepliesVisible(true);
					}}
					onCancel={() => setReplyTo(undefined)}
					parentId={postComment.id}
					replyTo={replyTo}
				/>
			) : null}
		</>
	);
};

const RepliesContainer = styled.div`
	position: relative;
	margin-left: 46px;
	margin-bottom: 10px;
`;

const ShowReplies = styled.div`
	cursor: pointer;
	${({ theme }) => theme.textStyles.label};
	padding-bottom: 10px;
`;

const Wire = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "extended",
})<{ extended?: boolean }>`
	position: absolute;
	left: -28px;
	top: -20px;
	bottom: ${({ extended }) => (extended ? -31 : 37)}px;
	width: 1px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;

const StyledCommentEditor = styled(CommentEditor)`
	padding-bottom: 10px;
`;

const TeasingSubCommentView = styled(CommentView)`
	margin-bottom: 6px;
`;
